import React, { useEffect, useState } from 'react';
import CloseIcon from '../icons/CloseIcon';
import TextAreaField from './TextAreaField';
import Button from './Button';

const pxToRem = (value: number) => `${value / 16}rem`;

interface Student {
  id: string;
  uid: string;
  name: string;
  number?: number;
  grade?: number;
  class?: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  students: Student[];
  templateTitle?: string;
  templateDescription?: string;
  stampCount?: number;
  onSave: (data: {
    selectedStudentIds: string[];
    reason: string;
  }) => Promise<void> | void;
}

const GiveStampDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  students,
  templateTitle,
  templateDescription,
  stampCount,
  onSave,
}) => {
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedStudentIds([]);
      setReason(templateDescription || '');
      setIsSubmitting(false);
      return;
    }
    setReason(templateDescription || '');
  }, [isOpen, templateDescription]);

  const toggleStudent = (id: string) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedStudentIds(students.map((s) => s.id));
  };

  const handleClearSelection = () => {
    setSelectedStudentIds([]);
  };

  const handleSave = async () => {
    if (selectedStudentIds.length === 0 || !reason.trim()) return;
    setIsSubmitting(true);
    try {
      await onSave({
        selectedStudentIds,
        reason: reason.trim(),
      });
      onClose();
    } catch (error) {
      console.error('도장 지급 실패:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled = selectedStudentIds.length === 0 || !reason.trim() || isSubmitting;
  const allSelected = students.length > 0 && selectedStudentIds.length === students.length;

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        fontFamily: 'Pretendard, sans-serif',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: pxToRem(450),
          background: 'var(--Color-background-normal, white)',
          borderRadius: pxToRem(16),
          boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.06), 0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
          padding: `${pxToRem(24)} ${pxToRem(28)}`,
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(32),
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: pxToRem(8), width: '100%' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: pxToRem(2) }}>
            <div
              style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(20),
                fontWeight: 600,
                lineHeight: pxToRem(30),
              }}
            >
              도장 지급하기
            </div>
            {templateTitle ? (
              <div
                style={{
                  color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                  fontSize: pxToRem(14),
                  fontWeight: 500,
                  lineHeight: pxToRem(21),
                }}
              >
                {templateTitle}
                {stampCount != null ? ` · ${stampCount}도장` : ''}
              </div>
            ) : null}
          </div>
          <div
            onClick={onClose}
            role="button"
            tabIndex={0}
            aria-label="닫기"
            style={{ padding: pxToRem(4), cursor: 'pointer', flexShrink: 0 }}
          >
            <CloseIcon width={24} height={24} color="var(--Color-text-normal, #161616)" />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: pxToRem(12) }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: pxToRem(14), fontWeight: 600, color: 'var(--Color-text-normal)' }}>
              학생 선택 ({selectedStudentIds.length}/{students.length})
            </div>
            <button
              type="button"
              onClick={allSelected ? handleClearSelection : handleSelectAll}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--Color-text-brand-color, #FF6F0F)',
                fontSize: pxToRem(13),
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {allSelected ? '선택 해제' : '전체 선택'}
            </button>
          </div>
          <div
            style={{
              maxHeight: pxToRem(220),
              overflowY: 'auto',
              border: '1px solid var(--Color-line-normal, #F0F0F0)',
              borderRadius: pxToRem(8),
              padding: pxToRem(8),
            }}
            aria-label="도장을 받을 학생 목록"
          >
            {students.length === 0 ? (
              <div style={{ padding: pxToRem(12), color: 'var(--Color-text-assistive)', fontSize: pxToRem(13) }}>
                지급 대상 학생이 없습니다.
              </div>
            ) : (
              students.map((student) => {
                const checked = selectedStudentIds.includes(student.id);
                return (
                  <label
                    key={student.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: pxToRem(8),
                      padding: `${pxToRem(8)} ${pxToRem(4)}`,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleStudent(student.id)}
                    />
                    <span style={{ fontSize: pxToRem(14), color: 'var(--Color-text-normal)' }}>
                      {student.grade != null && student.class != null
                        ? `${student.grade}-${student.class} `
                        : ''}
                      {student.number != null ? `${student.number}번 ` : ''}
                      {student.name}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <TextAreaField
          value={reason}
          onChange={setReason}
          placeholder="도장 부여 사유를 입력해 주세요"
          width="100%"
          height={pxToRem(84)}
          size="small"
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: pxToRem(8) }}>
          <Button variant="cancel" size="medium" onClick={onClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button variant="submit" size="medium" onClick={handleSave} disabled={isSaveDisabled}>
            {isSubmitting
              ? '지급 중...'
              : selectedStudentIds.length > 0
                ? `도장 지급 (${selectedStudentIds.length}명)`
                : '도장 지급'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GiveStampDialog;
